/**
 * Text-to-Key Registry
 *
 * Maps English notification texts stored in DB to translation keys.
 * This enables translating old/existing notifications at render time.
 *
 * The registry is auto-generated from notifications.en.json to ensure
 * a single source of truth. Manual additions are only needed for:
 * - Legacy/alias texts (multiple texts mapping to the same key)
 * - Texts that differ from the current en.json values
 */

import { NOTIFICATION_KEYS } from "./notification-keys";
import notificationsEn from "./notifications.en.json";

export interface TranslationMapping {
  key: string;
  extractParams?: (text: string, metadata?: Record<string, unknown>) => Record<string, string>;
}

/**
 * Recursively builds a registry from a nested JSON object.
 * Maps each leaf string value to its dot-notation key path.
 *
 * Example:
 *   { emailVerification: { reminder: "Verify..." } }
 *   → { "Verify...": { key: "emailVerification.reminder" } }
 *
 * Skips strings containing {param} placeholders (handled by DYNAMIC_TEXT_PATTERNS).
 */
function buildRegistryFromJson(obj: Record<string, unknown>, prefix = ""): Record<string, TranslationMapping> {
  const registry: Record<string, TranslationMapping> = {};

  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === "string") {
      // Skip texts with parameters like "Try again in {seconds}s"
      // These need DYNAMIC_TEXT_PATTERNS instead
      if (!value.includes("{")) {
        registry[value] = { key: fullKey };
      }
    } else if (typeof value === "object" && value !== null) {
      // Recurse into nested objects
      Object.assign(registry, buildRegistryFromJson(value as Record<string, unknown>, fullKey));
    }
  }

  return registry;
}

// Auto-generated from en.json - single source of truth
const AUTO_REGISTRY = buildRegistryFromJson(notificationsEn);

/**
 * Manual additions for legacy/alias texts.
 * These are texts that exist in old DB records but differ from current en.json.
 * Multiple texts can map to the same translation key.
 */
const LEGACY_TEXT_ALIASES: Record<string, TranslationMapping> = {
  // Old variant stored in some DB records
  "Validate your email within 5 days to continue using WOW": {
    key: NOTIFICATION_KEYS.EMAIL_VERIFICATION_REMINDER,
  },
  // Legacy CTA button texts
  start: { key: NOTIFICATION_KEYS.CTA_VERIFY },
  Start: { key: NOTIFICATION_KEYS.CTA_VERIFY },
};

/**
 * Combined registry: auto-generated + legacy aliases.
 * Legacy aliases take precedence if there's a conflict.
 */
export const TEXT_TO_KEY_REGISTRY: Record<string, TranslationMapping> = {
  ...AUTO_REGISTRY,
  ...LEGACY_TEXT_ALIASES,
};

/**
 * Dynamic text patterns for texts with embedded values.
 * Used for texts like "Try again in 25s" where the number varies.
 */
export const DYNAMIC_TEXT_PATTERNS: Array<{
  pattern: RegExp;
  key: string;
  extractParams: (match: RegExpMatchArray) => Record<string, string>;
}> = [
  {
    pattern: /^Try again in (\d+)s$/,
    key: NOTIFICATION_KEYS.CTA_TRY_AGAIN,
    extractParams: (match) => ({ seconds: match[1] ?? "0" }),
  },
];
